import { useDispatch, useSelector } from 'react-redux';

import { filterSteps } from 'dashboard/components/WorkflowEditor/utils';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { WorkflowStep } from 'dashboard/models/workflow-version';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import { LocalStorageKey } from 'lib/clients/StorageClient';
import useLocalStorage from 'lib/hooks/useLocalStorage';

interface CopiedWorkflowSteps {
  organizationId: string;
  steps: WorkflowStep[];
  // `count` can differ from length of `steps` since it includes children steps.
  count: number;
}

const useCopyWorkflowSteps = () => {
  const dispatch = useDispatch();
  const organizationId = useSelector<RootState, string>(
    (state) => state.organization.organization?.id ?? '',
  );
  const [stored, storeSteps] = useLocalStorage<CopiedWorkflowSteps>(
    LocalStorageKey.DashboardWorkflowEditorCopiedSteps,
    { organizationId, steps: [], count: 0 },
    { sync: true },
  );

  const copy = (steps: WorkflowStep[]) => {
    const stepAvailableTracker = steps.reduce<Record<string, boolean>>(
      (tracker, { token }) => ({
        ...tracker,
        [token]: true,
      }),
      {},
    );

    const totalStepCount = steps.length;
    if (totalStepCount > 0) {
      dispatch(
        showMessage(
          totalStepCount > 1 ? `Copied ${totalStepCount} steps` : `Copied 1 step`,
          MessageType.Success,
        ),
      );
    }
    storeSteps({
      organizationId,
      steps: filterSteps(steps, stepAvailableTracker),
      count: totalStepCount,
    });
  };

  return [stored, { copy }] as const;
};

export default useCopyWorkflowSteps;
